import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Link, useLocation } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Search, Settings, User, XCircle, Bell } from "react-feather";
import { all_routes } from "../../Router/all_routes";
// getProfileTest removed - unused
import { getAllClients } from "../../core/api/settings";
import { getNotificationCount } from "../../core/api/actions";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { setSelectedClients, setClients } from "../../core/redux/action";
import { handleLogout } from "../../core/api/auth";
import { useNavigate } from "react-router-dom";
import {
  Select,
  Modal,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Form,
  Switch,
  Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import BrandSelector from "../../components/BrandSelector";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// Custom Currency Modal - same as InvoiceFormLayout
const CustomCurrencyModal = ({
  visible,
  onCancel,
  onSave,
  currencies,
  rates,
}) => {
  const [currencyList, setCurrencyList] = useState([]);

  useEffect(() => {
    if (visible) {
      // Initialize with default + custom currencies
      const defaultCurrencies = [
        { code: "USD", symbol: "$", rate: 1.0, isDefault: true },
        { code: "EUR", symbol: "€", isDefault: true },
        { code: "GBP", symbol: "£", isDefault: true },
      ];
      const customCurrencies = currencies
        .filter((c) => !defaultCurrencies.some((dc) => dc.code === c.code))
        .map((c) => ({ ...c, rate: rates[c.code] || "" }));

      const allCurrencies = defaultCurrencies.map((dc) => ({
        ...dc,
        rate: rates[dc.code] || dc.rate || "",
      }));

      // Add custom currencies not in default list
      customCurrencies.forEach((cc) => {
        if (!allCurrencies.some((ac) => ac.code === cc.code)) {
          allCurrencies.push(cc);
        }
      });

      setCurrencyList(allCurrencies.filter((c) => c.code !== "USD")); // Don't allow editing USD
    }
  }, [visible, currencies, rates]);

  const handleSave = () => {
    const newRates = {};
    const newCurrencies = [
      { code: "USD", symbol: "$" },
      ...currencyList.map(({ code, symbol }) => ({ code, symbol })),
    ];
    currencyList.forEach((c) => {
      if (c.rate) {
        newRates[c.code] = parseFloat(c.rate);
      }
    });
    onSave(newCurrencies, newRates);
    onCancel();
  };

  const handleCurrencyChange = (index, field, value) => {
    const newList = [...currencyList];
    newList[index][field] = value;
    setCurrencyList(newList);
  };

  const addCurrency = () => {
    setCurrencyList([
      ...currencyList,
      { code: "", symbol: "", rate: "", isDefault: false },
    ]);
  };

  const removeCurrency = (index) => {
    const newList = [...currencyList];
    newList.splice(index, 1);
    setCurrencyList(newList);
  };

  return (
    <Modal
      title="Add / Edit Custom Currencies"
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      width={600}
      zIndex={10600}
    >
      <Form layout="vertical">
        {currencyList.map((currency, index) => (
          <Row
            key={index}
            gutter={16}
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col span={6}>
              <Input
                placeholder="Code (e.g., INR)"
                value={currency.code}
                onChange={(e) =>
                  handleCurrencyChange(
                    index,
                    "code",
                    e.target.value.toUpperCase()
                  )
                }
                disabled={currency.isDefault}
              />
            </Col>
            <Col span={4}>
              <Input
                placeholder="Symbol"
                value={currency.symbol}
                onChange={(e) =>
                  handleCurrencyChange(index, "symbol", e.target.value)
                }
                disabled={currency.isDefault}
              />
            </Col>
            <Col span={10}>
              <InputNumber
                placeholder="Rate to 1 USD"
                value={currency.rate}
                onChange={(value) => handleCurrencyChange(index, "rate", value)}
                min={0}
                precision={3}
                style={{ width: "100%" }}
                addonBefore="1 USD ="
              />
            </Col>
            <Col span={4}>
              {!currency.isDefault && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeCurrency(index)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Button
          type="dashed"
          onClick={addCurrency}
          icon={<PlusOutlined />}
          style={{ marginTop: "16px" }}
        >
          Add Currency
        </Button>
      </Form>
    </Modal>
  );
};

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const Header = () => {
  const navigate = useNavigate();
  const selectedBrands =
    useSelector((state) => state.selectedBrands, shallowEqual) ?? EMPTY_ARRAY;
  const [notificationCount, setNotificationCount] = useState(0); // Will be updated from API
  const route = all_routes;
  const [toggle, SetToggle] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dispatch = useDispatch();

  // Currency state - matching CreateInvoiceModal / InvoiceFormLayout pattern
  const [customCurrencyModalVisible, setCustomCurrencyModalVisible] =
    useState(false);
  const [currencies, setCurrencies] = useState(() => {
    try {
      const saved = localStorage.getItem("custom_currencies");
      return saved
        ? JSON.parse(saved)
        : [
            { code: "USD", symbol: "$" },
            { code: "EUR", symbol: "€" },
            { code: "GBP", symbol: "£" },
          ];
    } catch {
      return [
        { code: "USD", symbol: "$" },
        { code: "EUR", symbol: "€" },
        { code: "GBP", symbol: "£" },
      ];
    }
  });
  const [customRates, setCustomRates] = useState(() => {
    try {
      const saved = localStorage.getItem("custom_currency_rates");
      if (saved) {
        return JSON.parse(saved);
      }
      // Default rates for common currencies
      const defaultRates = { EUR: 0.92, GBP: 0.79 };
      localStorage.setItem(
        "custom_currency_rates",
        JSON.stringify(defaultRates)
      );
      return defaultRates;
    } catch {
      return { EUR: 0.92, GBP: 0.79 };
    }
  });
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    try {
      return localStorage.getItem("global_display_currency") || "USD";
    } catch {
      return "USD";
    }
  });
  const [decimalPrecision, setDecimalPrecision] = useState(() => {
    try {
      const saved = localStorage.getItem("global_decimal_precision");
      return saved ? parseInt(saved, 10) : 3;
    } catch {
      return 3;
    }
  });

  // Handle saving custom currencies from modal
  const handleSaveCustomCurrencies = useCallback(
    (newCurrencies, newRates) => {
      setCurrencies(newCurrencies);
      setCustomRates(newRates);
      localStorage.setItem("custom_currencies", JSON.stringify(newCurrencies));
      localStorage.setItem("custom_currency_rates", JSON.stringify(newRates));
      // Dispatch event so other components can update
      window.dispatchEvent(
        new CustomEvent("currencyRatesChanged", { detail: { rates: newRates } })
      );
      window.dispatchEvent(
        new CustomEvent("globalCurrencyChanged", {
          detail: { currency: selectedCurrency },
        })
      );
    },
    [selectedCurrency]
  );

  // Handle currency selection from dropdown
  const handleCurrencyChange = useCallback((value) => {
    if (value === "add_edit") {
      setCustomCurrencyModalVisible(true);
      return;
    }
    setSelectedCurrency(value);
    localStorage.setItem("global_display_currency", value);
    window.dispatchEvent(
      new CustomEvent("globalCurrencyChanged", { detail: { currency: value } })
    );
  }, []);

  const handleDecimalToggle = useCallback((checked) => {
    const newVal = checked ? 2 : 3;
    setDecimalPrecision(newVal);
    localStorage.setItem("global_decimal_precision", String(newVal));
    window.dispatchEvent(
      new CustomEvent("globalDecimalChanged", { detail: { decimals: newVal } })
    );
  }, []);

  const selectedClients =
    useSelector((state) => state.selectedClients, shallowEqual) ?? EMPTY_ARRAY;
  const clients =
    useSelector((state) => state.clients, shallowEqual) ?? EMPTY_ARRAY;
  const profile =
    useSelector((state) => state.profile, shallowEqual) ?? EMPTY_OBJECT;
  console.log("Profile in Header component:", profile);
  const location = useLocation();
  const showBrandSelector = true; // Show brand selector on all pages
  const isEditMarketingPage = location.pathname.includes("/edit-marketing");
  const isEditMediaplanPage = location.pathname.includes("/edit-media-plan");
  const clientsSetOnceRef = useRef(false);
  const clientsFetchedRef = useRef(false); // Prevent duplicate clients API calls
  const notificationFetchedRef = useRef(false); // Prevent duplicate notification API calls

  const isAdmin = profile?.role === "super_admin";
  const clientPermissions = profile?.client_permission || [];

  const visibleClients = useMemo(() => {
    if (!Array.isArray(clients) || clients.length === 0) return [];
    return clients;
  }, [clients]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async (force = false) => {
      try {
        // Skip if already fetched on initial load (unless forced by interval)
        if (!force && notificationFetchedRef.current) return;
        notificationFetchedRef.current = true;

        // Get notification count from the API
        const count = await getNotificationCount();
        setNotificationCount(count);
      } catch (err) {
        console.error("Failed to load notifications", err);
      }
    };

    fetchNotifications();

    // Listen for notification count updates from Actions page
    const handleNotificationCountUpdate = (event) => {
      setNotificationCount(event.detail.count);
    };

    window.addEventListener(
      "notificationCountUpdated",
      handleNotificationCountUpdate
    );

    // Set up polling for notifications (every 5 minutes)
    const notificationInterval = setInterval(() => {
      fetchNotifications(true); // Force fetch on interval
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(notificationInterval);
      window.removeEventListener(
        "notificationCountUpdated",
        handleNotificationCountUpdate
      );
    };
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        if (!profile) return; // wait until profile is ready
        if (clientsFetchedRef.current) return; // Already fetched, skip

        clientsFetchedRef.current = true; // Mark as fetching to prevent duplicates
        const clientsRes = await getAllClients();
        dispatch(setClients(clientsRes.data));
      } catch (error) {
        console.error("Error fetching dropdown data", error);
        clientsFetchedRef.current = false; // Reset on error to allow retry
      }
    };

    fetchDropdownData();
  }, [dispatch, profile]);

  useEffect(() => {
    if (profile && visibleClients.length > 0) {
      try {
        const raw = localStorage.getItem("selectedClients");
        // Filter out any non-numeric values and ensure we only include valid client IDs
        const ids = raw
          ? JSON.parse(raw)
              .map(String)
              .filter(
                (id) =>
                  /^\d+$/.test(id) &&
                  visibleClients.some((c) => String(c.id) === id)
              )
          : [];

        dispatch(setSelectedClients(ids));
        clientsSetOnceRef.current = true;
      } catch (err) {
        console.error(
          "Failed to parse client_permission from localStorage",
          err
        );
        // If there's an error, set to empty array (no clients selected)
        dispatch(setSelectedClients([]));
        clientsSetOnceRef.current = true;
      }
    }
  }, [profile, visibleClients, dispatch]);

  useEffect(() => {
    if (
      profile?.role === "super_admin" &&
      visibleClients.length > 0 &&
      !clientsSetOnceRef.current &&
      selectedClients.length === 0
    ) {
      const allIds = visibleClients.map((c) => String(c.id));
      dispatch(setSelectedClients(allIds));
      clientsSetOnceRef.current = true;
    }
  }, [profile, visibleClients, selectedClients, dispatch]);

  const effectiveSelectedClientIds =
    selectedClients.length > 0
      ? selectedClients
      : profile?.role !== "super_admin" &&
        Array.isArray(profile?.client_permission)
      ? profile.client_permission.map(String)
      : [];

  const isElementVisible = (element) => {
    return element.offsetWidth > 0 || element.offsetHeight > 0;
  };

  useEffect(() => {
    const handleMouseover = (e) => {
      e.stopPropagation();

      const body = document.body;
      const toggleBtn = document.getElementById("toggle_btn");

      if (
        body.classList.contains("mini-sidebar") &&
        isElementVisible(toggleBtn)
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("mouseover", handleMouseover);

    return () => {
      document.removeEventListener("mouseover", handleMouseover);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);
  const handlesidebar = () => {
    document.body.classList.toggle("mini-sidebar");
    SetToggle((current) => !current);
  };
  const sidebarOverlay = () => {
    document?.querySelector(".main-wrapper")?.classList?.toggle("slide-nav");
    document?.querySelector(".sidebar-overlay")?.classList?.toggle("opened");
    document?.querySelector("html")?.classList?.toggle("menu-opened");
  };

  const exclusionArray = [
    "/reactjs/template/dream-pos/index-three",
    "/reactjs/template/dream-pos/index-one",
  ];
  if (exclusionArray.indexOf(window.location.pathname) >= 0) {
    return "";
  }

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };
  const logout = (e) => {
    e.preventDefault();
    dispatch(handleLogout());
    navigate("/signin");
  };

  return (
    <>
      <div className="header" id="myDiv">
        {/* Logo */}
        <div className={`header-left ${toggle ? "" : "active"}`}>
          <Link to="#" className="logo logo-normal" onClick={handlesidebar}>
            <ImageWithBasePath src="assets/img/logo.png" alt="img" />
          </Link>
          <Link to="#" className="logo logo-white" onClick={handlesidebar}>
            <ImageWithBasePath src="assets/img/logo-white.png" alt="img" />
          </Link>
          <Link to="#" className="logo-small" onClick={handlesidebar}>
            <ImageWithBasePath src="assets/img/logo-small.png" alt="img" />
          </Link>
          <Link id="toggle_btn" to="#" onClick={handlesidebar}>
            <FeatherIcon icon="chevrons-left" className="feather-16" />
          </Link>
        </div>
        {/* /Logo */}
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#"
          onClick={sidebarOverlay}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        {/* Header Menu */}
        <ul className="nav user-menu">
          {/* Search */}
          <li className="nav-item nav-searchinputs">
            <div className="top-nav-search">
              <Link to="#" className="responsive-search">
                <Search />
              </Link>
              <form action="#" className="dropdown">
                <div
                  className="searchinputs dropdown-toggle"
                  id="dropdownMenuClickable"
                >
                  <input type="text" placeholder="Search" />
                  <div className="search-addon">
                    <span>
                      <XCircle className="feather-14" />
                    </span>
                  </div>
                </div>
              </form>
            </div>
          </li>
          {/* /Search */}
          <li className="nav-item main-drop select-store-dropdown d-flex align-items-center px-2" style={{ flex: "0 1 auto", minWidth: 0 }}>
            <div style={{ position: "relative", width: 180 }}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%", minWidth: 140 }}
                placeholder="Select Client(s)"
                maxTagCount={1}
                maxTagPlaceholder={(omittedValues) =>
                  `+${omittedValues.length}`
                }
                labelInValue
                value={
                  selectedClients.length === 0
                    ? []
                    : selectedClients
                        .filter((id) =>
                          visibleClients.some(
                            (client) => String(client.id) === id
                          )
                        )
                        .map((id) => {
                          const client = visibleClients.find(
                            (c) => String(c.id) === id
                          );
                          return {
                            value: id,
                            label: client?.company_name,
                          };
                        })
                }
                tagRender={(props) => {
                  // Use selectedClients directly for consistency with the value prop
                  const validSelections = selectedClients.filter((id) =>
                    visibleClients.some((client) => String(client.id) === id)
                  );

                  // Handle case where no valid selections exist
                  if (validSelections.length === 0) {
                    return null;
                  }

                  // Show "All Clients" if all are selected
                  if (validSelections.length === visibleClients.length) {
                    // Only render for the first tag to avoid duplicates
                    const isFirstTag =
                      String(props.value) === String(validSelections[0]);
                    if (isFirstTag) {
                      return (
                        <span style={{ padding: "0 8px" }}>All Clients</span>
                      );
                    }
                    return null;
                  }

                  // For partial selection, show the label from props
                  return (
                    <span style={{ padding: "0 4px" }}>{props.label}</span>
                  );
                }}
                onChange={(values) => {
                  const ids = values.map((v) => v.value);
                  sessionStorage.removeItem("mediaPlanDraft");
                  sessionStorage.removeItem("invoiceDraft");
                  // Dispatch custom event BEFORE updating Redux to notify components to clear cache
                  window.dispatchEvent(
                    new CustomEvent("globalClientChanged", {
                      detail: { clientIds: ids },
                    })
                  );
                  dispatch(setSelectedClients(ids));
                  // Also save to localStorage for persistence
                  localStorage.setItem("selectedClients", JSON.stringify(ids));
                }}
                dropdownRender={(menu) => (
                  <>
                    <div
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        color: "#fd7e14",
                        fontWeight: 500,
                      }}
                      onClick={() => {
                        const allIds = visibleClients.map((c) => String(c.id));
                        const isAllSelected =
                          selectedClients.length === allIds.length;

                        const newSelection = isAllSelected ? [] : allIds;

                        // Dispatch custom event BEFORE updating Redux
                        window.dispatchEvent(
                          new CustomEvent("globalClientChanged", {
                            detail: { clientIds: newSelection },
                          })
                        );
                        dispatch(setSelectedClients(newSelection));
                        // Save to localStorage for persistence
                        localStorage.setItem(
                          "selectedClients",
                          JSON.stringify(newSelection)
                        );
                      }}
                    >
                      {selectedClients.length === visibleClients.length
                        ? "Deselect All"
                        : "Select All"}
                    </div>
                    {menu}
                  </>
                )}
                dropdownStyle={{ zIndex: 10500 }}
                getPopupContainer={(trigger) => trigger.parentNode}
                disabled={isEditMarketingPage || isEditMediaplanPage}
              >
                {visibleClients.map((client) => (
                  <Select.Option
                    key={client.id}
                    value={String(client.id)}
                    label={client.company_name}
                  >
                    {client.company_name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {/* Global Brand Selector */}
            <div style={{ marginLeft: 8 }}>
              {showBrandSelector && <BrandSelector />}
            </div>

            {/* Currency Converter - Dropdown with Add/Edit option like CreateInvoiceModal */}
            <div style={{ marginLeft: 8 }}>
              <Select
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                style={{ width: 130 }}
                dropdownStyle={{ zIndex: 10500 }}
              >
                {currencies.map((c) => (
                  <Select.Option key={c.code} value={c.code}>
                    {c.symbol} - {c.code}{" "}
                    {customRates[c.code] && c.code !== "USD"
                      ? `(${customRates[c.code]})`
                      : ""}
                  </Select.Option>
                ))}
                <Select.Option key="add_edit" value="add_edit">
                  <span style={{ color: "#1890ff" }}>
                    <PlusOutlined /> Add / Edit Currencies
                  </span>
                </Select.Option>
              </Select>
            </div>
          </li>
          {/* Decimal Precision Toggle */}
          <li className="nav-item nav-item-box" style={{ padding: "0 4px" }}>
            <Tooltip title={`Round figures to ${decimalPrecision === 2 ? "2" : "3"} decimal places`}>
              <div style={{ display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                <span style={{ fontSize: 11, color: "#666" }}>3</span>
                <Switch
                  size="small"
                  checked={decimalPrecision === 2}
                  onChange={handleDecimalToggle}
                />
                <span style={{ fontSize: 11, color: "#666" }}>2dp</span>
              </div>
            </Tooltip>
          </li>

          <li className="nav-item nav-item-box">
            <Link
              to="#"
              id="btnFullscreen"
              onClick={() => toggleFullscreen()}
              className={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
            >
              {/* <i data-feather="maximize" /> */}
              <FeatherIcon icon="maximize" />
            </Link>
          </li>

          {/* Notification Bell */}
          <li className="nav-item nav-item-box">
            <Link to="/actions" className="notification-box position-relative">
              <Bell />
              {notificationCount > 0 && (
                <span
                  className="badge rounded-pill bg-danger position-absolute"
                  style={{ top: "-5px", right: "-5px", fontSize: "10px" }}
                >
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </Link>
          </li>

          <li className="nav-item dropdown has-arrow main-drop">
            <Link
              to="#"
              className="dropdown-toggle nav-link userset"
              data-bs-toggle="dropdown"
            >
              <span className="user-info">
                <span className="user-detail">
                  <span className="user-name">
                    {profile?.first_name} {profile?.last_name}{" "}
                  </span>
                  <span className="user-role">{profile?.role}</span>
                </span>
              </span>
            </Link>
            <div className="dropdown-menu menu-drop-user">
              <div className="profilename">
                <div className="profileset">
                  <span className="user-img">
                    <ImageWithBasePath src="assets/img/profile.png" alt="img" />
                    <span className="status online" />
                  </span>
                  <div className="profilesets">
                    <h6>
                      {profile?.first_name} {profile?.last_name}
                    </h6>
                    <h5>{profile?.role}</h5>
                  </div>
                </div>
                <hr className="m-0" />
                <Link className="dropdown-item" to={route.profile}>
                  <User className="me-2" /> My Profile
                </Link>
                <Link
                  hidden={true}
                  className="dropdown-item"
                  to={route.generalsettings}
                >
                  <Settings className="me-2" />
                  Settings
                </Link>
                <hr className="m-0" />
                <Link className="dropdown-item logout pb-0" onClick={logout}>
                  <ImageWithBasePath
                    src="assets/img/icons/log-out.svg"
                    alt="img"
                    className="me-2"
                  />
                  Logout
                </Link>
              </div>
            </div>
          </li>
        </ul>
        {/* /Header Menu */}
        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link className="dropdown-item" to="profile">
              My Profile
            </Link>
            <Link className="dropdown-item" to="generalsettings">
              Settings
            </Link>
            <Link className="dropdown-item" to="signin">
              Logout
            </Link>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>

      {/* Currency Modal */}
      <CustomCurrencyModal
        visible={customCurrencyModalVisible}
        onCancel={() => setCustomCurrencyModalVisible(false)}
        onSave={handleSaveCustomCurrencies}
        currencies={currencies}
        rates={customRates}
      />
    </>
  );
};

export default Header;
